import * as React from 'react';

/** Material UI Packages. */
import { Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Internal Components. */
import { CustomAutocompleteCell_string } from 'shared/ui/RenderCell';
import { containerSizeList } from 'shared/config/constantsData';
import { createOptionPasteParser, createOptionNameValueParser } from 'shared/lib/datagridHelpers';

/** Column definitions for the air alias DataGrid. */
export const aliasCols = (aliasRow, autocompleteOptions, handleUnitChange, handleDeleteClick, onInvalidOption) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        hide: true,
    },
    {
        field: 'alias',
        headerName: 'Alias',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'il_opus_vessel_id',
        headerName: 'Flight#',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'quantity',
        headerName: 'Volume',
        align: 'center',
        headerAlign: 'center',
        flex: 0.5,
        editable: true,
        type: 'number',
    },
    {
        field: 'il_opus_volume_unit_id',
        headerName: 'Unit',
        align: 'center',
        headerAlign: 'center',
        flex: 1.5,
        editable: true,
        valueParser: createOptionNameValueParser(
            'il_opus_volume_unit_id',
            autocompleteOptions.il_opus_volume_unit.options,
            onInvalidOption
        ),
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_string
                    params={params}
                    options={autocompleteOptions.il_opus_volume_unit.options || []}
                    value={aliasRow.filter((item) => item.id == params.id)[0].il_opus_volume_unit_id || ''}
                    handleInputChange={(event, value) => handleUnitChange(event, value, 'il_opus_volume_unit')}
                    shouldFocus={false}
                />
            );
        },
    },
    {
        field: 'weight',
        headerName: 'Weight',
        align: 'center',
        headerAlign: 'center',
        flex: 0.5,
        editable: true,
        type: 'number',
        renderCell: (params) => `${params.value || 0} kg`,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        flex: 0.3,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
    },
];

/** Column definitions for the ocean container DataGrid. */
export const cntrCols = (handleDeleteClick, renderVolumeEditCell, onInvalidOption) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        hide: true,
    },
    {
        field: 'cntr_number',
        headerName: 'Container',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'volume',
        headerName: 'Volume',
        type: 'singleSelect',
        valueOptions: containerSizeList,
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        editable: true,
        pastedValueParser: createOptionPasteParser(containerSizeList, onInvalidOption),
        renderEditCell: renderVolumeEditCell,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        flex: 0.3,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
    },
];

/** Column definitions for the export container DataGrid. */
export const exportCntrCols = (handleDeleteClick, renderVolumeEditCell, onInvalidOption) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        hide: true,
    },
    {
        field: 'cntr_number',
        headerName: 'Container',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'seal_number',
        headerName: 'Seal',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'volume',
        headerName: 'Volume',
        type: 'singleSelect',
        valueOptions: containerSizeList,
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        editable: true,
        pastedValueParser: createOptionPasteParser(containerSizeList, onInvalidOption),
        renderEditCell: renderVolumeEditCell,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        flex: 0.3,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
    },
];

/** Column definitions for the trucking alias DataGrid. */
export const truckingAliasCols = (aliasRow, autocompleteOptions, handleUnitChange, handleDeleteClick, onInvalidOption) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        hide: true,
    },
    {
        field: 'alias',
        headerName: 'Alias',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },

    {
        field: 'quantity',
        headerName: 'Volume',
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        editable: true,
        type: 'number',
    },
    {
        field: 'il_opus_volume_unit_id',
        headerName: 'Unit',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
        valueParser: createOptionNameValueParser(
            'il_opus_volume_unit_id',
            autocompleteOptions.il_opus_volume_unit.options,
            onInvalidOption
        ),
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_string
                    params={params}
                    options={autocompleteOptions.il_opus_volume_unit.options || []}
                    value={aliasRow.filter((item) => item.id == params.id)[0].il_opus_volume_unit_id || ''}
                    handleInputChange={(event, value) => handleUnitChange(event, value, 'il_opus_volume_unit')}
                    shouldFocus={false}
                />
            );
        },
    },
    {
        field: 'weight',
        headerName: 'Weight',
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        editable: true,
        type: 'number',
        renderCell: (params) => `${params.value || 0} lb`,
    },
    {
        field: 'reference',
        headerName: 'Reference',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        flex: 0.3,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
    },
];
